"use client";

import { useState } from "react";
import {
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@tillix/ui-atoms/input-group";
import { EyeClosedIcon, EyeIcon } from "lucide-react";
import {
  Controller,
  type FieldValues,
  type Path,
  useFormContext,
} from "react-hook-form";
import { Link } from "@/components/atoms/link";
import { useT } from "@/features/i18n/hooks/use-t";

type PasswordInputProps = {
  name?: string;
  showForgotPasswordLink?: boolean;
};

export default function PasswordInput<TFieldValues extends FieldValues>({
  name = "password",
  showForgotPasswordLink = true,
}: PasswordInputProps) {
  const form = useFormContext<TFieldValues>();
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useT("inputs");
  const { t: tUi } = useT();

  const Icon = showPassword ? EyeIcon : EyeClosedIcon;
  const handleIconClick = () => setShowPassword((v) => !v);

  const labelKey =
    name === "currentPassword"
      ? "labels.currentPassword"
      : name === "confirmPassword"
        ? "labels.confirmPassword"
        : "labels.password";
  const label = t(labelKey);

  return (
    <Controller
      control={form.control}
      name={name as Path<TFieldValues>}
      render={({ field, fieldState }) => (
        <FieldRoot name={field.name}>
          <FieldLabel>{label}</FieldLabel>

          <FieldControl
            render={(defaultProps) => (
              <InputGroup>
                <InputGroupInput
                  {...defaultProps}
                  {...field}
                  autoComplete="password"
                  placeholder={t("placeholders.password")}
                  type={showPassword ? "text" : "password"}
                />
                <InputGroupAddon align="inline-end">
                  <button
                    aria-label={
                      showPassword ? `Hide ${label}` : `Show ${label}`
                    }
                    aria-pressed={showPassword}
                    className="rounded text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
                    onClick={handleIconClick}
                    type="button"
                  >
                    <Icon aria-hidden="true" size={20} />
                  </button>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <FieldError>
            {fieldState.error ? t("validation.passwordMinLength") : undefined}
          </FieldError>

          {showForgotPasswordLink && (
            <FieldDescription className="mt-1">
              <Link
                className="text-tenant-howard-county-primary-primary-blue text-xs underline hover:text-tenant-howard-county-primary-primary-blue/80"
                href="/forgot-password"
              >
                {tUi("auth.forgotPassword")}
              </Link>
            </FieldDescription>
          )}
        </FieldRoot>
      )}
    ></Controller>
  );
}
